import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { CurrencySymbol } from '@/components/ui/currency-symbol'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Pagination, getPageCount } from '@/components/ui/pagination'
import { useOrganization } from '@/contexts/OrganizationContext'
import { usePayoutStatements, type PayoutSectionLabel, getSectionLabelForStatus } from '@/hooks/usePayoutStatements'
import type { PayoutStatement } from '@/types/financials'
import { PayoutQuickDetailsDialog } from '@/components/financials/PayoutQuickDetailsDialog'
import { CheckCircle, Clock, RefreshCw, Search, Share, XCircle } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

const DEFAULT_PAGE_SIZE = 10

function formatDate(value?: string | null): string {
  if (!value) return 'N/A'
  const d = new Date(value)
  if (isNaN(d.getTime())) return 'N/A'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Get the total amount for a payout statement.
 * Supports both new backend format (grossAmount) and legacy format.
 */
function getTotal(statement: PayoutStatement): number {
  // Prefer grossAmount from new backend
  if (typeof statement.grossAmount === 'number') return statement.grossAmount
  // Fallback to totalAmount
  if (typeof statement.totalAmount === 'number') return statement.totalAmount
  // Legacy calculation
  return (statement.totalProviderCut || 0) + (statement.totalProviderVat || 0)
}

function StatusBadge({ label }: { label: PayoutSectionLabel | null }) {
  if (!label) return null
  const { t } = useLocalization()
  const map: Record<PayoutSectionLabel, { className: string; Icon: any; translationKey: string }> = {
    'Pending Approval': { className: 'bg-yellow-100 text-yellow-800', Icon: Clock, translationKey: 'financials.pendingApproval' },
    'In Progress': { className: 'bg-blue-100 text-blue-800', Icon: Share, translationKey: 'financials.inProgress' },
    Processed: { className: 'bg-green-100 text-green-800', Icon: CheckCircle, translationKey: 'financials.processed' },
    Disapproved: { className: 'bg-red-100 text-red-800', Icon: XCircle, translationKey: 'financials.disapproved' },
  }
  const { className, Icon, translationKey } = map[label]
  return (
    <Badge variant="secondary" className={className}>
      <Icon className="w-3 h-3 me-1" />
      {t(translationKey)}
    </Badge>
  )
}

export function FinancialsPage() {
  const { t } = useLocalization()
  const { currentOrganization } = useOrganization()
  const { statements, sections, summary, isLoading, isError, error, refetch, approve, dispute, isApproving, isDisputing } =
    usePayoutStatements()

  const [selectedStatement, setSelectedStatement] = useState<PayoutStatement | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [tab, setTab] = useState<'all' | 'pending' | 'inProgress' | 'processed' | 'disapproved'>('pending')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const actionsDisabled = isApproving || isDisputing

  const headerSubtitle = useMemo(() => {
    if (!currentOrganization) return t('financials.selectOrganization')
    return t('financials.managePayouts', { name: currentOrganization.displayName || currentOrganization.name })
  }, [currentOrganization, t])

  const getSearchText = (s: PayoutStatement) => {
    const providers = Array.isArray(s.payoutDetails)
      ? s.payoutDetails
          .map((d) => d.providerName || '')
          .filter(Boolean)
          .join(' ')
      : ''
    return `${s.id} ${providers}`.toLowerCase()
  }

  const filteredStatementsByTab = useMemo(() => {
    const q = search.trim().toLowerCase()
    const source =
      tab === 'all'
        ? statements
        : tab === 'pending'
          ? sections.pendingApproval
          : tab === 'inProgress'
            ? sections.inProgress
            : tab === 'processed'
              ? sections.processed
              : sections.disapproved

    if (!q) return source
    return source.filter((s) => getSearchText(s).includes(q))
  }, [search, tab, statements, sections.pendingApproval, sections.inProgress, sections.processed, sections.disapproved])

  // A tab or search change puts the user back on the first page.
  useEffect(() => {
    setPage(1)
  }, [tab, search])

  const pageCount = getPageCount(filteredStatementsByTab.length, pageSize)

  // Guard against landing past the last page (e.g. after a refetch shrinks the list).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])

  const paginatedStatements = useMemo(() => {
    const start = (Math.min(page, pageCount) - 1) * pageSize
    return filteredStatementsByTab.slice(start, start + pageSize)
  }, [filteredStatementsByTab, page, pageCount, pageSize])

  const tabMeta = useMemo(() => {
    const list =
      tab === 'all'
        ? statements
        : tab === 'pending'
          ? sections.pendingApproval
          : tab === 'inProgress'
            ? sections.inProgress
            : tab === 'processed'
              ? sections.processed
              : sections.disapproved

    const totalAmount = list.reduce((sum, s) => sum + getTotal(s), 0)
    return { count: list.length, totalAmount }
  }, [tab, statements, sections.pendingApproval, sections.inProgress, sections.processed, sections.disapproved])

  const renderSection = (title: string, icon: ComponentType<{ className?: string }>, items: PayoutStatement[]) => {
    const Icon = icon
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 bg-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-gray-500" />
            <span className="font-bold text-sm sm:text-base">{title}</span>
            <span className="ms-auto text-xs sm:text-sm text-muted-foreground">
              {tabMeta.count} • <CurrencySymbol amount={tabMeta.totalAmount} />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="bg-white rounded-b-xl">
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-lg border p-4 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-6 w-24" />
                </div>
              ))
            ) : items.length === 0 ? (
              <div className="text-center text-muted-foreground py-10">
                {t('financials.noStatements')}
              </div>
            ) : (
              items.map((s) => {
                const label = getSectionLabelForStatus(s.status)
                return (
                  <div
                    key={s.id}
                    className="rounded-lg border bg-white p-4 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedStatement(s)
                      setDetailsOpen(true)
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="font-mono text-xs text-gray-700 truncate flex-1">
                        {s.statementNumber || s.id}
                      </span>
                      <StatusBadge label={label} />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">
                        <CurrencySymbol amount={getTotal(s)} />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {s.payoutCount ?? (Array.isArray(s.payoutDetails) ? s.payoutDetails.length : 0)} {t('financials.transactionsShort')}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {formatDate(s.periodStart)} → {formatDate(s.periodEnd)}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('financials.statementId')}</TableHead>
                  <TableHead>{t('financials.amount')}</TableHead>
                  <TableHead>{t('financials.transactions')}</TableHead>
                  <TableHead>{t('financials.period')}</TableHead>
                  <TableHead>{t('financials.periodEnd')}</TableHead>
                  <TableHead>{t('financials.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-10" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-44" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-28" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  items.map((s) => {
                    const label = getSectionLabelForStatus(s.status)
                    return (
                      <TableRow
                        key={s.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          setSelectedStatement(s)
                          setDetailsOpen(true)
                        }}
                      >
                        <TableCell className="font-mono max-w-[260px] truncate">
                          {s.statementNumber || s.id}
                        </TableCell>
                        <TableCell>
                          <CurrencySymbol amount={getTotal(s)} />
                        </TableCell>
                        <TableCell>
                          {s.payoutCount ?? (Array.isArray(s.payoutDetails) ? s.payoutDetails.length : 0)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(s.periodStart)} → {formatDate(s.periodEnd)}
                        </TableCell>
                        <TableCell>{formatDate(s.periodEnd)}</TableCell>
                        <TableCell>
                          <StatusBadge label={label} />
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
                {!isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                      {t('financials.noStatements')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {!isLoading && filteredStatementsByTab.length > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              totalItems={filteredStatementsByTab.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="container mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold">{t('financials.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">{headerSubtitle}</p>
        </div>
      </div>

      {/* Error */}
      {isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center text-red-700">
              <XCircle className="w-5 h-5 me-2" />
              <div>
                <div className="font-medium">{t('financials.failedToLoad')}</div>
                <div className="text-sm">{(error as Error)?.message || t('common.unknownError')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('financials.pendingApproval')}</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold">{summary.pendingApprovalCount}</div>
              <div className="text-sm text-muted-foreground">
                <CurrencySymbol amount={summary.pendingApprovalAmount} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-8 w-8 rounded-lg bg-yellow-500/15 flex items-center justify-center">
                <Clock className="h-4 w-4 text-yellow-700" />
              </div>
              {t('financials.awaitingAction')}
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('financials.inProgress')}</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold">{summary.inProgressCount}</div>
              <div className="text-sm text-muted-foreground">
                <CurrencySymbol amount={summary.inProgressAmount} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-8 w-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                <Share className="h-4 w-4 text-blue-700" />
              </div>
              {t('financials.beingProcessed')}
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('financials.processed')}</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold">{summary.processedCount}</div>
              <div className="text-sm text-muted-foreground">
                <CurrencySymbol amount={summary.processedAmount} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-8 w-8 rounded-lg bg-green-500/15 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-green-700" />
              </div>
              {t('financials.paidSettled')}
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('financials.disapproved')}</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold">{summary.disapprovedCount}</div>
              <div className="text-sm text-muted-foreground">
                <CurrencySymbol amount={summary.disapprovedAmount} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-8 w-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-700" />
              </div>
              {t('financials.disputedStatements')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls + table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-3 sm:p-4 bg-white">
          <div className="flex flex-col gap-3">
            <div className="relative flex-1 sm:max-w-lg">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('financials.searchPlaceholder')}
                className="ps-10 bg-gray-50 border-gray-200"
              />
            </div>

            <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
              <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
                <TabsList className="bg-gray-100 w-max">
                  <TabsTrigger value="pending" className="gap-1 sm:gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                    <span className="hidden sm:inline">{t('financials.pendingApproval')}</span>
                    <span className="sm:hidden">{t('financials.pendingShort')}</span>
                    {summary.pendingApprovalCount > 0 && (
                      <Badge variant="secondary" className="ms-0.5 h-5 min-w-5 px-1.5 rounded-full text-xs">
                        {summary.pendingApprovalCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="inProgress" className="gap-1 sm:gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                    <span className="hidden sm:inline">{t('financials.inProgress')}</span>
                    <span className="sm:hidden">{t('financials.inProgressShort')}</span>
                    {summary.inProgressCount > 0 && (
                      <Badge variant="secondary" className="ms-0.5 h-5 min-w-5 px-1.5 rounded-full text-xs">
                        {summary.inProgressCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="processed" className="gap-1 sm:gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                    {t('financials.processed')}
                    {summary.processedCount > 0 && (
                      <Badge variant="secondary" className="ms-0.5 h-5 min-w-5 px-1.5 rounded-full text-xs">
                        {summary.processedCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="disapproved" className="gap-1 sm:gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                    {t('financials.disapproved')}
                    {summary.disapprovedCount > 0 && (
                      <Badge variant="secondary" className="ms-0.5 h-5 min-w-5 px-1.5 rounded-full text-xs">
                        {summary.disapprovedCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  {/* <TabsTrigger value="all" className="gap-1 sm:gap-1.5 text-xs sm:text-sm whitespace-nowrap">
                    {t('financials.all')}
                    {statements.length > 0 && (
                      <Badge variant="secondary" className="ms-0.5 h-5 min-w-5 px-1.5 rounded-full text-xs">
                        {statements.length}
                      </Badge>
                    )}
                  </TabsTrigger> */}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsContent value="pending" className="mt-4">
              {renderSection(t('financials.pendingApprovalStatements'), Clock, paginatedStatements)}
            </TabsContent>
            <TabsContent value="inProgress" className="mt-4">
              {renderSection(t('financials.inProgressStatements'), Share, paginatedStatements)}
            </TabsContent>
            <TabsContent value="processed" className="mt-4">
              {renderSection(t('financials.processedStatements'), CheckCircle, paginatedStatements)}
            </TabsContent>
            <TabsContent value="disapproved" className="mt-4">
              {renderSection(t('financials.disapprovedStatements'), XCircle, paginatedStatements)}
            </TabsContent>
            {/* <TabsContent value="all" className="mt-4">
              {renderSection(t('financials.allStatements'), RefreshCw, paginatedStatements)}
            </TabsContent> */}
          </Tabs>
        </CardContent>
      </Card>

      <PayoutQuickDetailsDialog
        statement={selectedStatement}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        actionsDisabled={actionsDisabled}
        onApprove={async (statementId, document, notes) => {
          await approve({ statementId, document, notes })
          setDetailsOpen(false)
        }}
        onDispute={async (statementId, reason, document) => {
          await dispute({ statementId, reason, document })
          setDetailsOpen(false)
        }}
      />
    </div>
  )
}
